/**
 * Soft-light camera pipeline.
 *
 * Takes the raw camera stream and returns a processed MediaStream whose video
 * track comes from a canvas. Every consumer (local preview, the WebRTC peer
 * connection and MediaRecorder) sees the same processed feed, so what the
 * partner sees and what lands in the recording match the preview.
 *
 * The subject stays sharp. A soft, elliptical mask over the middle of the frame
 * keeps the face out of the heavy blur: the background gets the full soft
 * wash, the subject gets only a whisper of smoothing plus a light crispening
 * pass so eyes, glasses and edges read clearly.
 */

/**
 * Strength levels. Blur is relative to frame width so it looks the same at any
 * resolution. `centerAlpha` is the smoothing allowed over the subject,
 * `edgeAlpha` the wash over the background, `crisp` the sharp re-draw that
 * pulls detail back into the face.
 */
export const SOFT_LEVELS = {
  off:    { label: "Off",    centerAlpha: 0,    edgeAlpha: 0,    blurRatio: 0,     brightness: 1,    saturate: 1,    crisp: 0 },
  soft:   { label: "Soft",   centerAlpha: 0.13, edgeAlpha: 0.34, blurRatio: 0.006, brightness: 1.02, saturate: 1.02, crisp: 0.30 },
  softer: { label: "Softer", centerAlpha: 0.22, edgeAlpha: 0.50, blurRatio: 0.010, brightness: 1.04, saturate: 1.03, crisp: 0.34 },
};

export const SOFT_ORDER = ["off", "soft", "softer"];

export function createSoftLightPipeline(rawStream, { level = "soft", fps = 30 } = {}) {
  const videoTrack = rawStream.getVideoTracks()[0];
  if (!videoTrack || typeof document === "undefined") return null;

  const settings = videoTrack.getSettings ? videoTrack.getSettings() : {};
  const width = settings.width || 1280;
  const height = settings.height || 720;

  const source = document.createElement("video");
  source.muted = true;
  source.playsInline = true;
  source.autoplay = true;
  source.srcObject = new MediaStream([videoTrack]);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx || typeof canvas.captureStream !== "function") return null;

  const state = { level: SOFT_LEVELS[level] ? level : "soft", stopped: false, raf: null, timer: null };
  const supportsFilter = "filter" in ctx;

  // Scratch layer used to build masked passes (background wash, crisp subject).
  const layer = document.createElement("canvas");
  layer.width = width;
  layer.height = height;
  const lctx = layer.getContext("2d");

  // Elliptical subject mask, centred slightly above the middle where a face
  // sits in a webcam frame. Built once — it only depends on frame size.
  const cx = width / 2;
  const cy = height * 0.44;
  const r = Math.min(width, height);
  const stretch = 1.28;
  const makeGradient = (inside, outside) => {
    const g = lctx.createRadialGradient(cx, cy, r * 0.26, cx, cy, r * 0.62);
    g.addColorStop(0, inside);
    g.addColorStop(1, outside);
    return g;
  };
  const eraseSubject = makeGradient("rgba(0,0,0,1)", "rgba(0,0,0,0)");
  const eraseBackground = makeGradient("rgba(0,0,0,0)", "rgba(0,0,0,1)");

  /** Draws `paint` into the scratch layer, punches it with `mask`, returns the layer. */
  const maskedLayer = (paint, mask) => {
    lctx.setTransform(1, 0, 0, 1, 0, 0);
    lctx.globalCompositeOperation = "source-over";
    lctx.clearRect(0, 0, width, height);
    paint();
    lctx.globalCompositeOperation = "destination-out";
    lctx.filter = "none";
    lctx.translate(cx, cy);
    lctx.scale(1, stretch);
    lctx.translate(-cx, -cy);
    lctx.fillStyle = mask;
    lctx.fillRect(-width, -height, width * 3, height * 3);
    lctx.setTransform(1, 0, 0, 1, 0, 0);
    lctx.globalCompositeOperation = "source-over";
    return layer;
  };

  const draw = () => {
    if (state.stopped) return;
    const w = canvas.width;
    const h = canvas.height;
    if (source.readyState < 2) {
      ctx.fillStyle = "#080808";
      ctx.fillRect(0, 0, w, h);
      return;
    }

    ctx.filter = "none";
    ctx.globalAlpha = 1;
    ctx.drawImage(source, 0, 0, w, h);

    const cfg = SOFT_LEVELS[state.level] || SOFT_LEVELS.off;
    if (cfg.edgeAlpha <= 0 || !supportsFilter) return;

    const blur = Math.max(3, Math.round(w * cfg.blurRatio));

    // 1. Whisper of smoothing across the whole frame — enough to even out skin,
    //    far too little to lose focus.
    if (cfg.centerAlpha > 0) {
      ctx.filter = `blur(${Math.max(2, Math.round(blur * 0.5))}px) brightness(${cfg.brightness}) saturate(${cfg.saturate})`;
      ctx.globalAlpha = cfg.centerAlpha;
      ctx.drawImage(source, 0, 0, w, h);
    }

    // 2. The real soft wash, masked away from the subject.
    maskedLayer(() => {
      lctx.filter = `blur(${blur * 2}px) brightness(${cfg.brightness}) saturate(${cfg.saturate})`;
      lctx.drawImage(source, 0, 0, w, h);
      lctx.filter = "none";
    }, eraseSubject);
    ctx.filter = "none";
    ctx.globalAlpha = cfg.edgeAlpha;
    ctx.drawImage(layer, 0, 0, w, h);

    // 3. Pull detail back into the subject so the face reads sharper than the
    //    surrounding wash.
    if (cfg.crisp > 0) {
      maskedLayer(() => {
        lctx.filter = "contrast(1.05) saturate(1.02)";
        lctx.drawImage(source, 0, 0, w, h);
        lctx.filter = "none";
      }, eraseBackground);
      ctx.globalAlpha = cfg.crisp;
      ctx.drawImage(layer, 0, 0, w, h);
    }

    ctx.filter = "none";
    ctx.globalAlpha = 1;
  };

  const loop = () => {
    draw();
    state.raf = requestAnimationFrame(loop);
  };

  source.play().catch(() => {});
  // requestAnimationFrame pauses in background tabs; a slow interval keeps the
  // recording alive (rather than freezing) if the user switches away.
  state.raf = requestAnimationFrame(loop);
  state.timer = setInterval(() => {
    if (document.hidden) draw();
  }, 1000 / 8);

  const canvasStream = canvas.captureStream(fps);
  const out = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...rawStream.getAudioTracks(),
  ]);

  return {
    stream: out,
    /** Change strength without touching the track, so no WebRTC renegotiation. */
    setLevel(v) { if (SOFT_LEVELS[v]) state.level = v; },
    get level() { return state.level; },
    stop() {
      state.stopped = true;
      if (state.raf) cancelAnimationFrame(state.raf);
      if (state.timer) clearInterval(state.timer);
      canvasStream.getTracks().forEach((t) => t.stop());
      source.srcObject = null;
    },
  };
}

const STORAGE_KEY = "boardroom_soft_light";

/** Stored preference. Legacy values "on"/"off" from the old toggle still work. */
export function readSoftLightLevel() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v || v === "on") return "soft";
    return SOFT_LEVELS[v] ? v : "off";
  } catch {
    return "soft";
  }
}

export function writeSoftLightLevel(level) {
  try {
    localStorage.setItem(STORAGE_KEY, SOFT_LEVELS[level] ? level : "soft");
  } catch { /* private mode — the default applies next time */ }
}

export function nextSoftLightLevel(level) {
  const i = SOFT_ORDER.indexOf(level);
  return SOFT_ORDER[(i + 1) % SOFT_ORDER.length];
}
